/* eslint-disable no-restricted-imports */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { AccountOrgAndContract } from '@/components/blocks/AccountOrgAndContract';
import { B2B } from '@/components/blocks/B2B';
import { Linkable } from '@/components/blocks/Linkable';
import {
	headerAccountItemSX,
	headerAccountLinkSX,
} from '@/components/content/Header/styles/account/item';
import { headerAccountOrgAndContractReadonlySX } from '@/components/content/Header/styles/account/orgAndContractReadonly';
import { useLogout } from '@/data/Content/Logout';
import { useLocalization } from '@/data/Localization';
import { useUser } from '@/data/User';
import { combineSX } from '@/utils/combineSX';
import { ExitToAppOutlined } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import { Divider, Skeleton, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { useDpvaleFlow } from '@/data/Content/Dpvale';
import { formatPrice } from 'integration/utils/formatPrice';
import StarsIcon from '@mui/icons-material/Stars';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { ACCOUNT_QUERY_TABS } from '@/components/content/Account';

export const HeaderAccountDropMenu: FC<{
	onClose: () => void;
}> = ({ onClose }) => {
	const RouteLocal = useLocalization('Routes');
	const { handleLogout } = useLogout();
	const { user } = useUser();
	const { isLoading: loadingDpValesDisponibles, currentSelectedDpvale } = useDpvaleFlow();

	return (
		<Stack
			width={290}
			spacing={1}
			sx={{
				backgroundColor: '#FFFFFF',
				borderBottomRightRadius: '6px',
				borderBottomLeftRadius: '6px',
				padding: '11px 20px 20px 20px',
			}}
		>
			<Stack direction="row" justifyContent="space-between" display="flex" alignItems="center">
				<Typography
					sx={{
						fontSize: '14px',
						color: '#2C2C2C',
						textAlign: 'left',
						flexGrow: 1,
					}}
				>
					{'Hola '}
					<Typography component="span" textTransform={'uppercase'} fontWeight={'bold'}>
						{`${user?.firstName},`}
					</Typography>
				</Typography>

				<ClearIcon
					sx={{
						fontSize: 'var(--font-size-body)',
						cursor: 'pointer',
						marginRight: '-6px',
						color: 'rgb(146, 146, 146)',
					}}
					onClick={onClose}
				/>
			</Stack>
			{currentSelectedDpvale ? (
				<Stack spacing={1}>
					<Stack>
						<Typography sx={{ fontSize: 'var(--font-size-caption)' }}>
							{'Distribuidor seleccionado'}
						</Typography>
						<Typography
							sx={{
								fontSize: 'var(--font-size-body)',
								color: '#2C2C2C',
								fontWeight: 'bold',
								WebkitLineClamp: 2,
								display: '-webkit-box',
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							}}
						>
							{currentSelectedDpvale?.distributor.name}
						</Typography>
					</Stack>
					<Stack>
						<Typography sx={{ fontSize: 'var(--font-size-caption)' }}>{'Saldo dpvale'}</Typography>
						<Typography
							sx={{
								fontSize: 'var(--font-size-body)',
								display: 'flex',
								alignItems: 'center',
								color: 'primary.main',
								fontWeight: 'bold',
								gap: 0.25,
							}}
						>
							<StarsIcon
								sx={{
									fontSize: 'var(--font-size-body)',
									alignSelf: 'center',
								}}
							/>
							{formatPrice(currentSelectedDpvale?.amount, 'MXN')}
						</Typography>
					</Stack>
					<DpButton
						href={{
							pathname: `/${RouteLocal.Account.route.t()}`,
							query: { tab: ACCOUNT_QUERY_TABS.DPVALE },
						}}
						variant="text"
						sx={{
							alignSelf: 'flex-start',
							textDecoration: 'underline',
						}}
					>
						{'Cambiar distribuidor'}
					</DpButton>
				</Stack>
			) : loadingDpValesDisponibles ? (
				<Stack spacing={1}>
					<Stack>
						<Skeleton variant="text" width="100%" height="20px" />
						<Skeleton variant="text" width="90%" height="20px" />
					</Stack>
					<Stack>
						<Skeleton variant="text" width="100%" height="20px" />
						<Skeleton variant="text" width="90%" height="20px" />
					</Stack>
				</Stack>
			) : null}
			<Stack>
				<B2B>
					<Stack
						divider={<Divider />}
						direction="row"
						alignItems="center"
						justifyContent="flex-start"
						spacing={1}
						sx={combineSX([headerAccountItemSX, headerAccountOrgAndContractReadonlySX])}
					>
						<AccountOrgAndContract />
					</Stack>
				</B2B>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-start"
					spacing={1}
					paddingY={'10px'}
					sx={headerAccountLinkSX}
				>
					<PersonIcon />
					<Linkable
						sx={{
							marginLeft: '12px !important',
						}}
						href={`/${RouteLocal.Account.route.t()}`}
						id={RouteLocal.Account.route.t()}
						data-testid={RouteLocal.Account.route.t()}
					>
						<Typography>{'Mi cuenta'}</Typography>
					</Linkable>
				</Stack>
				{/* {settings?.csrSession ? null : ( */}
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="flex-start"
					spacing={1}
					paddingY={0}
					sx={headerAccountLinkSX}
				>
					<ExitToAppOutlined />
					<Linkable
						sx={{
							marginLeft: '12px !important',
						}}
						type="link"
						href="/"
						onClick={handleLogout}
					>
						<Typography>{'Cerrar sesión'}</Typography>
					</Linkable>
				</Stack>
				{/* )} */}
			</Stack>
		</Stack>
	);
};
